import { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Users, Plus, Trash2, UserCircle } from "lucide-react";
import { toast } from "sonner";

export default function CrewManagement() {
  const { orgId } = useAuth();
  const [crews, setCrews] = useState<any[]>([]);
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCrew, setEditingCrew] = useState<any>(null);

  // Form state
  const [name, setName] = useState("");
  const [foremanId, setForemanId] = useState("none");
  const [status, setStatus] = useState("available");
  const [hourlyRate, setHourlyRate] = useState("");
  const [dailyCapacity, setDailyCapacity] = useState("1");

  useEffect(() => {
    if (orgId) {
      loadData();
    }
  }, [orgId]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load crews
      const { data: crewsData, error: crewsError } = await supabase
        .from("crews")
        .select("*, foreman:profiles!crews_foreman_id_fkey(id, full_name)")
        .eq("org_id", orgId)
        .order("name");

      if (crewsError) throw crewsError;
      setCrews(crewsData || []);

      // Load profiles for foreman selection
      const { data: profilesData, error: profilesError } = await supabase
        .from("profiles")
        .select("id, full_name")
        .eq("org_id", orgId)
        .order("full_name");

      if (profilesError) throw profilesError;
      setProfiles(profilesData || []);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Failed to load crews");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const crewData = {
        name,
        foreman_id: foremanId === "none" ? null : foremanId,
        status,
        hourly_rate: hourlyRate ? parseFloat(hourlyRate) : null,
        daily_capacity: parseInt(dailyCapacity),
        org_id: orgId,
      };

      if (editingCrew) {
        const { error } = await supabase
          .from("crews")
          .update(crewData)
          .eq("id", editingCrew.id);

        if (error) throw error;
        toast.success("Crew updated successfully");
      } else {
        const { error } = await supabase.from("crews").insert(crewData);

        if (error) throw error;
        toast.success("Crew created successfully");
      }

      resetForm();
      setDialogOpen(false);
      loadData();
    } catch (error) {
      console.error("Error saving crew:", error);
      toast.error("Failed to save crew");
    }
  };

  const handleEdit = (crew: any) => {
    setEditingCrew(crew);
    setName(crew.name);
    setForemanId(crew.foreman_id || "none");
    setStatus(crew.status);
    setHourlyRate(crew.hourly_rate?.toString() || "");
    setDailyCapacity(crew.daily_capacity?.toString() || "1");
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure you want to delete this crew?")) return;

    try {
      const { error } = await supabase.from("crews").delete().eq("id", id);

      if (error) throw error;
      toast.success("Crew deleted");
      loadData();
    } catch (error) {
      console.error("Error deleting crew:", error);
      toast.error("Failed to delete crew");
    }
  };

  const resetForm = () => {
    setEditingCrew(null);
    setName("");
    setForemanId("none");
    setStatus("available");
    setHourlyRate("");
    setDailyCapacity("1");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "available":
        return "default";
      case "on_job":
        return "secondary";
      case "off":
        return "destructive";
      default:
        return "default";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-muted-foreground">Loading crews...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="w-8 h-8" />
            Crew Management
          </h1>
          <p className="text-muted-foreground">Manage your work crews and assignments</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} className="gap-2">
              <Plus className="w-4 h-4" />
              Add Crew
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCrew ? "Edit Crew" : "Create New Crew"}</DialogTitle>
              <DialogDescription>
                Set up a crew with a foreman and work capacity
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label>Crew Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Crew Alpha, Install Team 1"
                  required
                />
              </div>

              <div>
                <Label>Foreman (Optional)</Label>
                <Select value={foremanId} onValueChange={setForemanId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select foreman" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Foreman</SelectItem>
                    {profiles.map((profile) => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.full_name || "Unnamed User"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="on_job">On Job</SelectItem>
                    <SelectItem value="off">Off Duty</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Hourly Rate (Optional)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={hourlyRate}
                  onChange={(e) => setHourlyRate(e.target.value)}
                  placeholder="0.00"
                />
              </div>

              <div>
                <Label>Daily Capacity (Jobs)</Label>
                <Input
                  type="number"
                  value={dailyCapacity}
                  onChange={(e) => setDailyCapacity(e.target.value)}
                  min="1"
                  required
                />
              </div>

              <Button type="submit" className="w-full">
                {editingCrew ? "Update Crew" : "Create Crew"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {crews.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-lg font-medium mb-2">No crews yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Create your first crew to start assigning jobs
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {crews.map((crew) => (
            <Card key={crew.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="flex items-center gap-2">
                      {crew.name}
                      <Badge variant={getStatusColor(crew.status)}>
                        {crew.status.replace("_", " ")}
                      </Badge>
                    </CardTitle>
                    {crew.foreman && (
                      <CardDescription className="flex items-center gap-1 mt-1">
                        <UserCircle className="w-4 h-4" />
                        {crew.foreman.full_name || "Unnamed Foreman"}
                      </CardDescription>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(crew)}
                    >
                      <Users className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(crew.id)}
                      className="text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-2">
                {crew.hourly_rate && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Hourly Rate:</span>
                    <span className="font-medium">${crew.hourly_rate}/hr</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Daily Capacity:</span>
                  <span className="font-medium">{crew.daily_capacity} jobs</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
